import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "@/lib/dateUtils";
import { listSessions } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  listLocalSessions,
  type LocalSessionSummary,
} from "@/lib/extension";

export function SessionsPage() {
  const { isSignedIn } = useAuthStore();
  const navigate = useNavigate();

  // Query server-side sessions only when signed in.
  const { data, isLoading, error } = useQuery({
    queryKey: ["sessions"],
    queryFn: () => listSessions({ limit: 50 }),
    enabled: isSignedIn,
  });

  // Query the extension for locally-buffered sessions regardless of sign-in
  // status — the whole point is to surface captures that haven't been
  // uploaded yet.
  const [localSessions, setLocalSessions] = useState<LocalSessionSummary[]>([]);
  const [extensionChecked, setExtensionChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void listLocalSessions().then((sessions) => {
      if (!cancelled) {
        setLocalSessions(sessions);
        setExtensionChecked(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Don't redirect away from /sessions when signed-out if the extension has
  // local sessions to show. Otherwise the user has nothing to interact with
  // until they sign in.
  useEffect(() => {
    if (isSignedIn) return;
    if (!extensionChecked) return;
    if (localSessions.length === 0) {
      void navigate("/", { replace: true });
    }
  }, [isSignedIn, extensionChecked, localSessions.length, navigate]);

  const sessions = data?.items ?? [];
  const hasLocal = localSessions.length > 0;
  const hasServer = sessions.length > 0;

  // Loading state — show skeletons while either the server query or the
  // extension check is in flight.
  if ((isSignedIn && isLoading) || !extensionChecked) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Sessions</h1>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  if (isSignedIn && error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-destructive">Failed to load sessions.</p>
      </div>
    );
  }

  // Genuinely empty state — signed in, no server sessions, no local sessions.
  if (isSignedIn && !hasServer && !hasLocal) {
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center text-center gap-4">
        <h1 className="text-2xl font-bold">No sessions yet</h1>
        <p className="text-muted-foreground max-w-md">
          Install the extension and start browsing to capture your first session.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      <h1 className="text-2xl font-bold">Sessions</h1>

      {/* Local-only sessions — captured while signed out, waiting to upload. */}
      {hasLocal && (
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">Buffered locally</h2>
              <p className="text-sm text-muted-foreground">
                {isSignedIn
                  ? "These sessions live in your extension's storage. They'll upload on the next sync."
                  : `Captured while signed out (${localSessions.length} session${localSessions.length === 1 ? "" : "s"}). Sign in to upload them.`}
              </p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {localSessions.map((s) => (
              <Card
                key={s.id}
                className="border-dashed h-full opacity-90"
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base line-clamp-1">
                      {s.name || "Untitled session"}
                    </CardTitle>
                    <Badge variant="outline" className="shrink-0">
                      Local
                    </Badge>
                  </div>
                  <CardDescription>
                    {formatDistanceToNow(new Date(s.started_at))}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-muted-foreground">
                    {s.pendingEventCount} event
                    {s.pendingEventCount === 1 ? "" : "s"} queued for upload
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Server sessions — only meaningful when signed in. */}
      {isSignedIn && hasServer && (
        <section className="space-y-3">
          {hasLocal && (
            <h2 className="text-lg font-semibold">Uploaded sessions</h2>
          )}
          <Tabs defaultValue="cards">
            <TabsList className="mb-4">
              <TabsTrigger value="cards">Cards</TabsTrigger>
              <TabsTrigger value="table">Table</TabsTrigger>
            </TabsList>

            <TabsContent value="cards">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {sessions.map((session) => (
                  <Link key={session.id} to={`/sessions/${session.id}`}>
                    <Card className="hover:border-primary transition-colors cursor-pointer h-full">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between gap-2">
                          <CardTitle className="text-base line-clamp-1">
                            {session.name || "Untitled session"}
                          </CardTitle>
                          {session.savedAt && (
                            <Badge variant="secondary" className="shrink-0">
                              Saved
                            </Badge>
                          )}
                        </div>
                        <CardDescription>
                          {session.startedAt
                            ? formatDistanceToNow(
                                new Date(
                                  Number(session.startedAt.seconds) * 1000,
                                ),
                              )
                            : "Unknown time"}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex gap-4 text-sm text-muted-foreground">
                          <span>{session.nodeCount} pages</span>
                          <span>{session.edgeCount} links</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Pages</TableHead>
                    <TableHead>Links</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((session) => (
                    <TableRow
                      key={session.id}
                      className="cursor-pointer"
                      onClick={() => void navigate(`/sessions/${session.id}`)}
                    >
                      <TableCell className="font-medium">
                        {session.name || "Untitled session"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {session.startedAt
                          ? formatDistanceToNow(
                              new Date(Number(session.startedAt.seconds) * 1000),
                            )
                          : "—"}
                      </TableCell>
                      <TableCell>{session.nodeCount}</TableCell>
                      <TableCell>{session.edgeCount}</TableCell>
                      <TableCell>
                        {session.savedAt ? (
                          <Badge variant="secondary">Saved</Badge>
                        ) : (
                          <Badge variant="outline">Recent</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>
          </Tabs>
        </section>
      )}
    </div>
  );
}
